import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Typography,
  Spin,
  message,
  Tooltip,
  Badge,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { getInformeOrdenCompraGeneral } from "../../../services/informes/ordenCompraSostenibilidadService";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const InformeOrdenesCompra = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedOrden, setSelectedOrden] = useState("");
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [dateRange, setDateRange] = useState([]);

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await getInformeOrdenCompraGeneral();
      if (response.estado) {
        setData(response.data);
        setFilteredData(response.data);
        message.success("Datos cargados correctamente");
      } else {
        message.error("Error al cargar los datos");
      }
    } catch (error) {
      message.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrar datos
  useEffect(() => {
    let filtered = data;

    // Filtro por texto (número de orden y descripción del producto)
    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.productoPO.toLowerCase().includes(searchText.toLowerCase()) ||
          item.numero_orden.toLowerCase().includes(searchText.toLowerCase()) ||
          item.nombreProveedor.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filtro por número de orden específico
    if (selectedOrden) {
      filtered = filtered.filter((item) => item.numero_orden === selectedOrden);
    }

    // Filtro por proveedor
    if (selectedProveedor) {
      filtered = filtered.filter(
        (item) => item.nombreProveedor === selectedProveedor
      );
    }

    // Filtro por rango de fechas
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.fechaRegistro);
        return (
          itemDate.isAfter(startDate.startOf("day")) &&
          itemDate.isBefore(endDate.endOf("day"))
        );
      });
    }

    setFilteredData(filtered);
  }, [searchText, selectedOrden, selectedProveedor, dateRange, data]);

  // Formatear números
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("es-CO").format(value);
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  // Obtener opciones únicas para filtros
  const numerosOrden = [...new Set(data.map((item) => item.numero_orden))];
  const proveedores = [...new Set(data.map((item) => item.nombreProveedor))];

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalOrdenadoSinIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_total_unitario),
      0
    );
    const totalOrdenadoConIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_total_iva),
      0
    );
    const totalEntregadoSinIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_total_entregado),
      0
    );
    const totalEntregadoConIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_total_entregado_iva),
      0
    );
    const totalIVA = filteredData.reduce(
      (sum, item) => sum + (item.valorIVA ? parseFloat(item.valorIVA) : 0),
      0
    );
    const promedioAvance = (totalEntregadoSinIva * 100) / totalOrdenadoSinIva;
    const totalProductos = filteredData.length;

    const totalCantidadUnidadesEntrantes = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.cantidad_entrada),
      0
    );

    const totalCantidadUnidadesEntregadas = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.total_entregado),
      0
    );

    const promedioAvanceCantidadUnidades = (
      (totalCantidadUnidadesEntregadas * 100) /
      totalCantidadUnidadesEntrantes
    ).toFixed(1);

    return {
      totalOrdenadoSinIva,
      totalOrdenadoConIva,
      totalEntregadoSinIva,
      totalEntregadoConIva,
      totalIVA,
      promedioAvance,
      totalProductos,
      totalOrdenes: new Set(filteredData.map((item) => item.numero_orden)).size,
      totalCantidadUnidadesEntrantes,
      totalCantidadUnidadesEntregadas,
      promedioAvanceCantidadUnidades,
    };
  };

  const estadisticas = calcularEstadisticas();

  // Exportar a Excel - Versión Mejorada
  const exportToExcel = () => {
    try {
      // Validar que existan datos para exportar
      if (!filteredData || filteredData.length === 0) {
        message.warning("No hay datos para exportar");
        return;
      }

      // Preparar los datos para Excel con validaciones
      const excelData = filteredData
        .map((row, index) => {
          try {
            return {
              "Número Orden": row.numero_orden || "",
              "Fecha Registro": formatDate(row.fechaRegistro) || "",
              Proveedor: row.nombreProveedor || "",
              "Dirección Proveedor": row.direccionProveedor || "",
              "Descripción Producto": row.productoPO || "",
              Cantidad: safeParseInt(row.cantidad_entrada),
              "Valor Unitario Sin IVA": safeParseFloat(row.valor_unitario),
              "Valor Total Sin IVA": safeParseFloat(row.valor_total_unitario),
              "IVA %": safeParseFloat(row.IVA),
              "Valor IVA": safeParseFloat(row.valor_iva),
              "Valor Unitario Con IVA": safeParseFloat(row.valor_unitario_iva),
              "Valor Total Con IVA": safeParseFloat(row.valor_total_iva),
              "Valor Total IVA": safeParseFloat(
                row.valor_total_iva - row.valor_total_unitario
              ),
              "Total Asignado": safeParseInt(row.total_asignado),
              "Total Entregado": safeParseInt(row.total_entregado),
              "Valor Entregado Sin IVA": safeParseFloat(
                row.valor_total_entregado
              ),
              "Valor Entregado Con IVA": safeParseFloat(
                row.valor_total_entregado_iva
              ),
              "Valor total IVA - Entregado": safeParseInt(
                row.valor_total_entregado_iva - row.valor_total_entregado
              ),

              "Porcentaje Avance": `${safeParseFloat(
                row.porcentaje_avance
              ).toFixed(2)}%`,
            };
          } catch (error) {
            console.warn(`Error procesando fila ${index}:`, error);
            return null;
          }
        })
        .filter((row) => row !== null); // Filtrar filas que fallaron

      // Funciones auxiliares para parsing seguro
      function safeParseInt(value, defaultValue = 0) {
        const parsed = parseInt(value);
        return isNaN(parsed) ? defaultValue : parsed;
      }

      function safeParseFloat(value, defaultValue = 0) {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultValue : parsed;
      }

      // Usar solo los datos sin fila de totales

      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();

      // Crear la hoja principal
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Configurar ancho de columnas optimizado
      const columnWidths = [
        { wch: 16 }, // Número Orden
        { wch: 14 }, // Fecha Registro
        { wch: 35 }, // Proveedor
        { wch: 30 }, // Dirección Proveedor
        { wch: 50 }, // Descripción Producto
        { wch: 12 }, // Cantidad
        { wch: 18 }, // Valor Unitario Sin IVA
        { wch: 18 }, // Valor Total Sin IVA
        { wch: 10 }, // IVA %
        { wch: 15 }, // Valor IVA
        { wch: 20 }, // Valor Unitario Con IVA
        { wch: 18 }, // Valor Total Con IVA
        { wch: 18 }, // Valor Total IVA
        { wch: 15 }, // Total Asignado
        { wch: 15 }, // Total Entregado
        { wch: 22 }, // Valor Entregado Sin IVA
        { wch: 22 }, // Valor Entregado Con IVA
        { wch: 22 }, // Valor total IVA - Entregado

        { wch: 16 }, // Porcentaje Avance
      ];

      worksheet["!cols"] = columnWidths;

      // Agregar solo la hoja principal al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, "Órdenes de Compra");

      // Generar nombre de archivo descriptivo
      const fecha = new Date().toISOString().split("T")[0];
      const nombreArchivo = `informe_ordenes_compra_${fecha}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(workbook, nombreArchivo);

      // Mensaje de éxito con información útil
      message.success({
        content: `Archivo exportado exitosamente: "${nombreArchivo}"`,
        duration: 5,
        style: { marginTop: "20px" },
      });
    } catch (error) {
      console.error("❌ Error al generar el archivo Excel:", error);
      message.error({
        content:
          "Error al generar el archivo Excel. Por favor, intente nuevamente.",
        duration: 5,
      });
    }
  };

  // Definir columnas de la tabla
  const columns = [
    {
      title: "Fecha Registro",
      dataIndex: "fechaRegistro",
      key: "fechaRegistro",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <div>
          <Text strong style={{ color: "#1890ff" }}>
            {formatDate(record.fechaRegistro)}
          </Text>
        </div>
      ),
    },
    {
      title: "Orden",
      dataIndex: "numero_orden",
      key: "numero_orden",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: "Producto",
      dataIndex: "productoPO",
      key: "productoPO",
      width: 350,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            <Text>{text}</Text>
            <br />
            <Tag size="small" color="geekblue">
              ID: {record.po_producto_oid}
            </Tag>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Proveedor",
      dataIndex: "nombreProveedor",
      key: "nombreProveedor",
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            <Badge status="processing" text={text} />
            <br />
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {record.direccionProveedor}
            </Text>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Cantidades",
      children: [
        {
          title: "Entrada",
          dataIndex: "cantidad_entrada",
          key: "cantidad_entrada",
          width: 80,
          align: "center",
          render: (text) => <Text strong>{formatNumber(text)}</Text>,
        },
        {
          title: "Asignado",
          dataIndex: "total_asignado",
          key: "total_asignado",
          width: 90,
          align: "center",
          render: (text) => formatNumber(text),
        },
        {
          title: "Entregado",
          dataIndex: "total_entregado",
          key: "total_entregado",
          width: 90,
          align: "center",
          render: (text) => formatNumber(text),
        },
        {
          title: "Disponible",
          key: "disponible",
          width: 90,
          align: "center",
          render: (_, record) => {
            // Convertir a número
            const entrada = Number(record.cantidad_entrada ?? 0);
            const entregado = Number(record.total_entregado ?? 0);
            const asignado = Number(record.total_asignado ?? 0);

            const disponible = entrada - (entregado + asignado);

            return formatNumber(disponible);
          },
        },
      ],
    },
    {
      title: "Valores de Orden",
      children: [
        {
          title: "Unit. Sin IVA",
          dataIndex: "valor_unitario",
          key: "valor_unitario",
          width: 120,
          align: "right",
          render: (text) => formatCurrency(text),
        },

        {
          title: "Total Sin IVA",
          dataIndex: "valor_total_unitario",
          key: "valor_total_unitario",
          width: 130,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#52c41a" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },

        {
          title: "IVA",
          dataIndex: "IVA",
          key: "IVA",
          width: 110,
          align: "right",
          render: (text) => (text != null ? <Text>{text + "%"}</Text> : null),
        },

        {
          title: "Valor IVA",
          dataIndex: "valor_iva",
          key: "valor_iva",
          width: 140,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#1890ff" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },

        {
          title: "Unit. Con IVA",
          dataIndex: "valor_unitario_iva",
          key: "valor_unitario_iva",
          width: 120,
          align: "right",
          render: (text) => (
            <Text style={{ color: "#1890ff" }}>{formatCurrency(text)}</Text>
          ),
        },

        {
          title: "Total Con IVA",
          dataIndex: "valor_total_iva",
          key: "valor_total_iva",
          width: 140,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#1890ff" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },

        {
          title: "Valor Total IVA",
          key: "valor_Total_iva",
          width: 120,
          align: "right",
          render: (text, record) => (
            <Text style={{ color: "#1890ff" }}>
              {formatCurrency(
                record.valor_total_iva - record.valor_total_unitario
              )}
            </Text>
          ),
        },
      ],
    },
    {
      title: "Valores Entregados",
      children: [
        {
          title: "Sin IVA",
          dataIndex: "valor_total_entregado",
          key: "valor_total_entregado",
          width: 130,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#722ed1" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },
        {
          title: "Con IVA",
          dataIndex: "valor_total_entregado_iva",
          key: "valor_total_entregado_iva",
          width: 140,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#52c41a" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },

        {
          title: "Total IVA",
          key: "valor_total_IVA",
          width: 140,
          align: "right",
          render: (text, record) => (
            <Text strong style={{ color: "#0958d9" }}>
              {formatCurrency(
                record.valor_total_entregado_iva - record.valor_total_entregado
              )}
            </Text>
          ),
        },
      ],
    },
    {
      title: "Avance",
      dataIndex: "porcentaje_avance",
      key: "porcentaje_avance",
      width: 120,
      align: "center",
      render: (text) => {
        const percentage = parseFloat(text);
        let status = "exception";
        if (percentage > 75) status = "success";
        else if (percentage > 25) status = "active";

        return (
          <Progress
            type="line"
            size={"small"}
            percent={percentage}
            status={status}
            format={() => `${percentage.toFixed(1)}%`}
          />
        );
      },
    },
  ];

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <Title level={2} style={{ marginBottom: "24px", color: "#1890ff" }}>
        🛒 Informe de Órdenes de Compra
      </Title>
      {/* Filtros y controles */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} lg={9}>
            <Input
              placeholder="Buscar por producto, orden, proveedor..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Select
              placeholder="Número de orden"
              style={{ width: "100%" }}
              value={selectedOrden}
              onChange={setSelectedOrden}
              allowClear
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
            >
              {numerosOrden.map((orden) => (
                <Option key={orden} value={orden}>
                  {orden}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Select
              placeholder="Proveedor"
              style={{ width: "100%" }}
              value={selectedProveedor}
              onChange={setSelectedProveedor}
              allowClear
              showSearch
              optionFilterProp="children" // busca dentro del texto de las opciones
            >
              {proveedores.map((proveedor) => (
                <Option key={proveedor} value={proveedor}>
                  {proveedor.length > 30
                    ? proveedor.substring(0, 30) + "..."
                    : proveedor}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={4}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Actualizar
              </Button>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={exportToExcel}
                disabled={filteredData.length === 0}
                style={{
                  backgroundColor: "#52c41a",
                  color: "#fff",
                  borderColor: "#52c41a",
                }}
              >
                Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tarjetas de estadísticas */}
      <Row gutter={16} style={{ marginBottom: "24px" }} align="stretch">
        <Col xs={24} sm={12} lg={2}>
          <Card
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Statistic
              title="Total Órdenes"
              value={estadisticas.totalOrdenes}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: "#1890ff", fontSize: "18px"  }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <Card
            bordered
            bodyStyle={{ padding: "16px" }}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Título principal a la izquierda */}
                <span>📦 Total Productos</span>

              </div>
            }
          >
            {/* Dos columnas para cantidades */}
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Entrantes"
                  value={estadisticas.totalCantidadUnidadesEntrantes}
                  valueStyle={{ color: "#1890ff", fontSize: "18px" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Entregadas"
                  value={estadisticas.totalCantidadUnidadesEntregadas}
                  valueStyle={{ color: "#52c41a", fontSize: "18px"  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Card
            bordered
            bodyStyle={{ padding: "16px" }}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Título principal a la izquierda */}
                <span>💰 Valor Entrante</span>

                {/* Valor IVA a la derecha, subtítulo + Tag */}
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    Valor IVA
                  </div>
                  <Tag
                    color="blue"
                    style={{ fontWeight: 600, fontSize: "14px" }}
                  >
                    {formatCurrency(
                      (estadisticas.totalOrdenadoConIva ?? 0) -
                        (estadisticas.totalOrdenadoSinIva ?? 0)
                    )}
                  </Tag>
                </div>
              </div>
            }
          >
            {/* Columnas para Sin IVA / Con IVA */}
            <Row gutter={16} justify="center" align="middle">
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Sin IVA"
                  value={estadisticas.totalOrdenadoSinIva}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                />
              </Col>
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Con IVA"
                  value={estadisticas.totalOrdenadoConIva}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#1890ff", fontSize: "18px" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={7}>
          <Card
            bordered
            bodyStyle={{ padding: "16px" }}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Título principal a la izquierda */}
                <span>💰 Valor Entregado</span>

                {/* Valor IVA a la derecha, subtítulo + Tag */}
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    Valor IVA
                  </div>
                  <Tag
                    color="blue"
                    style={{ fontWeight: 600, fontSize: "14px" }}
                  >
                    {formatCurrency(
                      (estadisticas.totalEntregadoConIva ?? 0) -
                        (estadisticas.totalEntregadoSinIva ?? 0)
                    )}
                  </Tag>
                </div>
              </div>
            }
          >
            <Row gutter={16} justify="center" align="middle">
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Sin IVA"
                  value={estadisticas.totalEntregadoSinIva}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                />
              </Col>
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title="Con IVA"
                  value={estadisticas.totalEntregadoConIva}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ color: "#1890ff",  fontSize: "18px"  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <Card
            title="Avance promedio"
            bordered
            bodyStyle={{ padding: "16px" }}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Row gutter={16} justify="center" align="middle">
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title={
                    <>
                      <ShoppingCartOutlined
                        style={{ marginRight: 6, color: "#52c41a" }}
                      />
                      Unidades
                    </>
                  }
                  value={estadisticas.promedioAvanceCantidadUnidades}
                  precision={1}
                  suffix="%"
                  valueStyle={{
                    color: "#52c41a",
                    fontSize: "18px",
                    fontWeight: 600,
                  }}
                />
              </Col>
              <Col span={12} style={{ textAlign: "center" }}>
                <Statistic
                  title={
                    <>
                      <DollarOutlined
                        style={{ marginRight: 6, color: "#1890ff" }}
                      />
                      Dinero
                    </>
                  }
                  value={estadisticas.promedioAvance}
                  precision={1}
                  suffix="%"
                  valueStyle={{
                    color: "#1890ff",
                    fontSize: "18px",
                    fontWeight: 600,
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Tabla de datos */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="lote_id"
            scroll={{ x: 1800, y: 600 }}
            pagination={{
              total: filteredData.length,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["1", "15", "20", "50"], // 👈 aquí defines las opciones
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
            }}
            size="small"
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>TOTALES</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text strong>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) => sum + parseInt(item.cantidad_entrada),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text strong>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) => sum + parseInt(item.total_asignado),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="center">
                    <Text strong>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) => sum + parseInt(item.total_entregado),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>

                  <Table.Summary.Cell index={6} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_total_unitario),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  <Table.Summary.Cell index={9}></Table.Summary.Cell>

                  <Table.Summary.Cell index={10} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) => sum + parseFloat(item.valor_total_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={11} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum +
                            (parseFloat(item.valor_total_iva || 0) -
                              parseFloat(item.valor_total_unitario || 0)),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={12} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_total_entregado),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={13} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_total_entregado_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={14} align="right">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum +
                            (parseFloat(item.valor_total_entregado_iva || 0) -
                              parseFloat(item.valor_total_entregado || 0)),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default InformeOrdenesCompra;
